import React, { createContext, useContext, useState, useRef } from "react";
import { useNotification } from "../notification-context";
import { postAuthenticatedRequest } from "../../../utils/api/requests/request";
import { readFeatureTogglesFromCache } from "../../../utils/helper/feature-toggle";
import { MODULE_CODE_TO_FEATURE_FLAG } from "../../../shared/constants/constants";
import { PATH_REQUIREMENTS } from "../../../shared/constants/pathRequirement";
import { readPermissionFlagsFromCache } from "../../../utils/helper/permission-toggle";

const MenuContext = createContext();

export const MenuProvider = ({ children }) => {
  const { triggerNotification } = useNotification();
  const [useV2Design, setUseV2Design] = useState(true);

  const [isFetchingMenuSchema, setIsFetchingMenuSchema] = useState(true);
  const [menuList, setMenuList] = useState(null);
  const isFetchingRef = useRef(false);

  // LocalStorage cache settings for menu list
  const MENU_CACHE_KEY = "__menu_schema__";
  const MENU_CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes

  const readMenuFromCache = () => {
    try {
      const raw = window.localStorage.getItem(MENU_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      if (
        typeof parsed.expiresAt !== "number" ||
        parsed.expiresAt <= Date.now()
      ) {
        window.localStorage.removeItem(MENU_CACHE_KEY);
        return null;
      }
      return parsed.value ?? null;
    } catch (_) {
      return null;
    }
  };

  const writeMenuToCache = (value, ttlMs = MENU_CACHE_TTL_MS) => {
    try {
      const payload = {
        value,
        expiresAt:
          Date.now() + Math.max(1000, Number(ttlMs || MENU_CACHE_TTL_MS)),
      };
      window.localStorage.setItem(MENU_CACHE_KEY, JSON.stringify(payload));
    } catch (_) {
      // ignore
    }
  };

  const canAccess = (path, enabledFeatures, userPermissions) => {
    const req = PATH_REQUIREMENTS[path];
    if (!req) return true; // unrestricted

    // Guard menu access by features
    const featureOk =
      !req.features ||
      req.features.length === 0 ||
      req.features.some((feature) => enabledFeatures.has(feature));

    //  temporarily allow all permissions
    // const permissionOk = true;

    // Guard menu access by permissions
    const permissionOk =
      !req.permissions ||
      req.permissions.length === 0 ||
      // Allow access if the user has AT LEAST ONE of the required permissions for this route.

      req.permissions.some((permission) =>
        userPermissions.some((p) => p.permissionCode === permission)
      );

    // Strict AND rule: must pass both checks
    return featureOk && permissionOk;
  };
  const fetchMenuSchemas = async () => {
    // Prevent concurrent calls
    if (isFetchingRef.current) {
      console.log(
        "fetchMenuSchemas already in progress, skipping duplicate call"
      );
      return;
    }

    try {
      // Try cache first
      const cachedMenu = readMenuFromCache();
      if (cachedMenu) {
        setMenuList(cachedMenu);
        setIsFetchingMenuSchema(false);
        return;
      }

      isFetchingRef.current = true;
      setIsFetchingMenuSchema(true);
      const data = {
        configName: "AdminPortalMenuConfiguration",
        path: "application",
      };
      const response = await postAuthenticatedRequest("GetConfigQuery", data);

      const result = response?.outData;

      if (result?.isSuccessful) {
        // Build enabled feature set from cache and filter menus accordingly
        const featuresFromCache = readFeatureTogglesFromCache() || [];

        const enabledFeaCodes = new Set(
          featuresFromCache
            .map((feature) => feature?.featureCode)
            .filter((code) => typeof code === "string" && code.length > 0)
        );

        const userPermissionsFromCache = readPermissionFlagsFromCache() || [];
        // const userPermissions = new Set(userPermissionsFromCache);

        const filteredMenus = result?.data?.menus
          ?.map((menu) => {
            const originalSubmenus = menu.submenus || [];
            // handle permissions for pages
            const keptSubmenus = originalSubmenus
              .filter((submenu) =>
                canAccess(
                  submenu?.path,
                  enabledFeaCodes,
                  userPermissionsFromCache
                )
              )
              .map((submenu) => ({
                ...submenu,
                path: submenu?.viewId
                  ? `/views/${submenu.viewId}`
                  : submenu?.path ?? "",
              }));

            if (keptSubmenus.length === 0) return null;
            return { ...menu, submenus: keptSubmenus };
          })
          .filter(Boolean);

        //   menu = {
        //     "code": "contacts",
        //     "label": "Contacts",
        //     "icon": "ContactsOutlined",
        //     "type": "page",
        //     "menu_groups": [
        //         "AdminCustomers"
        //     ],
        //     "translationCode": "common.Menu.Contacts",
        //     "submenus": [
        //         {
        //             "code": "ViewContact",
        //             "label": "Contact List",
        //             "icon": "UnorderedListOutlined",
        //             "path": "/contact-lists",
        //             "type": "page",
        //             "translationCode": "common.Menu.ContactList"
        //         },
        //         {
        //             "code": "NewContact",
        //             "label": "Contact",
        //             "icon": "PlusCircleOutlined",
        //             "path": "/new-contact",
        //             "type": "page",
        //             "translationCode": "common.Menu.Contacts"
        //         },
        //         {
        //             "code": "communications",
        //             "label": "Communications",
        //             "icon": "PhoneOutlined",
        //             "path": "/communications",
        //             "type": "page",
        //             "translationCode": "common.Menu.Communications"
        //         }
        //     ]
        // }

        //   module = {
        //     "code": "AdminCustomers",
        //     "name": "Contacts",
        //     "icon": "UsergroupAddOutlined",
        //     "navigationColor": "#f5222d",
        //     "translationCode": "common.Module.Menu.Contacts"
        // }

        // Filter modules using feature flags mapping. Also only show module that has at least one of the submenu
        const filteredModules = (result?.data?.modules || []).filter(
          (module) => {
            const requiredFeature = MODULE_CODE_TO_FEATURE_FLAG?.[module?.code];
            return (
              typeof requiredFeature === "string" &&
              requiredFeature.length > 0 &&
              enabledFeaCodes.has(requiredFeature) &&
              filteredMenus.some((menu) =>
                menu?.menu_groups.includes(module?.code)
              )
            );
          }
        );

        const filteredMenuList = {
          ...result?.data,
          menus: filteredMenus,
          modules: filteredModules,
        };
        setMenuList(filteredMenuList);
        writeMenuToCache(filteredMenuList);
      } else {
        triggerNotification({
          type: "error",
          message: "Error",
          description: result?.message,
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);

      triggerNotification({
        type: "error",
        message: "Error",
        description: error,
      });
    } finally {
      setIsFetchingMenuSchema(false);
      isFetchingRef.current = false;
    }
  };
  return (
    <MenuContext.Provider
      value={{
        menuList,
        isFetchingMenuSchema,
        setUseV2Design,
        useV2Design,
        fetchMenuSchemas,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
};

export const useMenu = () => {
  return useContext(MenuContext);
};
